import { useState } from 'react'

import {
  type GenericFormText,
  INTAKE_FORM_ERROR_CODES,
  type UpdatedGenericForm,
} from '@/definitions/intakeForm'
import { useUpdateGenericFormText } from '@/hooks/useUpdateGenericFormText'
import { isTimeoutError } from '@/utils/apiErrors'

import { describeSaveFailure } from '../utils/describeSaveFailure'
import { isFormGone } from '../utils/isFormGone'
import { readFieldErrors } from '../utils/readFieldErrors'
import { readRefusal } from '../utils/readRefusal'

/** The fields the box has an input for, and the order a refusal lists them in. */
export const EDIT_GENERIC_FORM_FIELDS = [
  'title',
  'intro_text',
  'completion_text',
] as const

export interface EditGenericFormValues {
  title: string
  intro_text: string
  completion_text: string
}

// Said here because the API's 404 is its generic not-found, worded for a log.
const FORM_GONE_MESSAGE =
  'This form is no longer there — it was removed after this box was opened. ' +
  'Close the box and reload the list.'

// The API treats a repeat of a landed save as a no-op, but refuses any other change
// built off the revision that save superseded — so only a verbatim retry is safe.
const SAVE_UNCONFIRMED_MESSAGE =
  'The server did not answer in time, so whether this was saved is not known. ' +
  'Saving the same text again is safe; to change anything else, close the box ' +
  'and reopen it.'

interface EditCallbacks {
  /** A title another of the organization's forms uses — reported on that field. */
  onTitleRefused: (message: string) => void
  /** Per-field reasons from a schema refusal, reported on the fields themselves. */
  onFieldsRefused: (
    byField: Partial<Record<keyof EditGenericFormValues, string>>
  ) => void
  /** The form as saved. `enabled` is the state the new revision carries. */
  onSaved: (saved: UpdatedGenericForm) => void
}

/**
 * The save flow behind the edit box: what to send, and what a refusal means.
 *
 * Nothing here decides what is acceptable — every verdict comes from the API,
 * and the job here is only to route each one to where its remedy is: a field
 * when the API named one, the alert when it named the submission as a whole.
 */
export const useEditGenericForm = (
  orgId: string,
  baseFormId: string,
  currentText: GenericFormText,
  callbacks: EditCallbacks
) => {
  const update = useUpdateGenericFormText()

  const [problem, setProblem] = useState<string | null>(null)
  const [reasons, setReasons] = useState<string[]>([])

  // A refusal describes the text it was raised against, so an edit retires it
  // and the next press is judged afresh.
  const retireProblem = () => {
    setProblem(null)
    setReasons([])
  }

  const submit = async (values: EditGenericFormValues) => {
    // Untouched values are sent byte-identical: the API skips its checks on a value
    // equal to the stored one, and trimming could break that match.
    const send = (field: keyof EditGenericFormValues) =>
      values[field] === currentText[field] ? values[field] : values[field].trim()
    const data = {
      title: send('title'),
      intro_text: send('intro_text'),
      completion_text: send('completion_text'),
      // All three are replaced on save, so the API refuses one built off a
      // revision since superseded rather than reverting the untouched fields.
      form_version: currentText.form_version,
    }

    try {
      const saved = await update.mutateAsync({ orgId, baseFormId, data })
      retireProblem()
      callbacks.onSaved(saved)
    } catch (error) {
      // Schema refusals carry no code and name their field in `detail`, so they
      // are read first and land on the inputs rather than in the alert.
      const byField = readFieldErrors(error, EDIT_GENERIC_FORM_FIELDS)
      if (Object.keys(byField).length > 0) {
        retireProblem()
        callbacks.onFieldsRefused(byField)
        return
      }

      const named = readRefusal(error)
      if (named?.code === INTAKE_FORM_ERROR_CODES.DUPLICATE_FORM_NAME) {
        retireProblem()
        callbacks.onTitleRefused(named.message)
        return
      }

      if (isFormGone(error)) {
        setReasons([])
        setProblem(FORM_GONE_MESSAGE)
        return
      }
      if (isTimeoutError(error)) {
        setReasons([])
        setProblem(SAVE_UNCONFIRMED_MESSAGE)
        return
      }
      // The reasons are the actionable half of a text refusal — the message is an
      // umbrella that names neither the field nor the construct. The service
      // sends one entry per finding so they can be listed rather than joined.
      setReasons(named?.errors?.map((issue) => issue.message) ?? [])
      setProblem(named?.message ?? describeSaveFailure(error))
    }
  }

  return {
    problem,
    reasons,
    isSaving: update.isPending,
    retireProblem,
    submit,
  }
}
